import { z } from 'zod';
import { newId } from '../ids.js';
import { blockRowSchema, instantSchema, uuidSchema } from '../schema/rows.js';
import { DomainError, isActionableKind } from './types.js';
/**
 * Minimal block commands of F0 (docs/09 §3: `createBlock`, `updateBlock`, `completeBlock`).
 * Each validates its input with the shared row schemas and the client-side invariants
 * (INV-03, INV-06) and returns the local operations to apply. The server revalidates.
 */
const createBlockInput = z.object({
    id: uuidSchema.optional(),
    workspace_id: uuidSchema,
    created_by: uuidSchema,
    kind: blockRowSchema.shape.kind.default('task'),
    title: z.string().trim().min(1, 'El título no puede estar vacío').max(2000),
    body: z.string().nullable().default(null),
    parent_id: uuidSchema.nullable().default(null),
    stage_id: uuidSchema.nullable().default(null),
    sort_key: z.string().min(1),
    due_on: blockRowSchema.shape.due_on.default(null),
    /** Instant of creation as ISO string; defaults to now. */
    now: instantSchema.optional(),
});
/** Creates a block. Actionable kinds start `open` (INV-03); other kinds have no status. */
export function createBlock(input) {
    const parsed = createBlockInput.parse(input);
    const actionable = isActionableKind(parsed.kind);
    if (!actionable && parsed.stage_id !== null) {
        throw new DomainError('invariant_violation', `Un bloque de tipo ${parsed.kind} no tiene etapa.`, 'INV-03');
    }
    const id = (parsed.id ?? newId());
    const now = parsed.now ?? new Date().toISOString();
    return [
        {
            table: 'blocks',
            op: 'PUT',
            id,
            data: {
                workspace_id: parsed.workspace_id,
                parent_id: parsed.parent_id,
                sort_key: parsed.sort_key,
                kind: parsed.kind,
                title: parsed.title,
                body: parsed.body,
                stage_id: parsed.stage_id,
                stage_changed_at: parsed.stage_id ? now : null,
                status: actionable ? 'open' : null,
                due_on: parsed.due_on,
                created_by: parsed.created_by,
                props: {},
                created_at: now,
            },
        },
    ];
}
const updateBlockTitleInput = z.object({
    id: uuidSchema,
    title: z.string().trim().min(1, 'El título no puede estar vacío').max(2000),
});
/** Changes only the title (PATCH with a single column: two devices editing different fields converge, S1). */
export function updateBlockTitle(input) {
    const parsed = updateBlockTitleInput.parse(input);
    return [{ table: 'blocks', op: 'PATCH', id: parsed.id, data: { title: parsed.title } }];
}
const completeBlockInput = z.object({
    block: z.object({
        id: uuidSchema,
        kind: blockRowSchema.shape.kind,
        status: blockRowSchema.shape.status,
    }),
    now: instantSchema.optional(),
});
/** Marks an actionable block as done with `completed_at` (INV-03, INV-06). Recurrence arrives in F2. */
export function completeBlock(input) {
    const { block, now } = completeBlockInput.parse(input);
    if (!isActionableKind(block.kind) || block.status === null) {
        throw new DomainError('invariant_violation', `Solo los bloques accionables (item, task, project) se pueden completar; ${block.kind} no lo es.`, 'INV-03');
    }
    if (block.status === 'done') {
        return [];
    }
    return [
        {
            table: 'blocks',
            op: 'PATCH',
            id: block.id,
            data: { status: 'done', completed_at: now ?? new Date().toISOString() },
        },
    ];
}
/** Reopens a done block: `completed_at` back to null with `status = open` (INV-06). */
export function reopenBlock(input) {
    const { block } = completeBlockInput.parse(input);
    if (!isActionableKind(block.kind) || block.status === null) {
        throw new DomainError('invariant_violation', `Solo los bloques accionables se pueden reabrir; ${block.kind} no lo es.`, 'INV-03');
    }
    if (block.status !== 'done')
        return [];
    return [
        {
            table: 'blocks',
            op: 'PATCH',
            id: block.id,
            data: { status: 'open', completed_at: null },
        },
    ];
}
//# sourceMappingURL=blocks.js.map